/**
 * 摄像头与麦克风的选择。
 *
 * 老系统没有这个：设备是系统默认的哪一个就用哪一个。外接摄像头、
 * 会议麦克风一插上，默认设备常常不是想用的那个，而课上才发现就晚了。
 *
 * 选择结果记在本地：一间教室里的设备通常不变，
 * 每次上课都要重选一遍比不能选也好不了多少。
 */

const KEY_CAM = 'whatylive.deviceId.camera'
const KEY_MIC = 'whatylive.deviceId.mic'
const KEY_SPK = 'whatylive.deviceId.speaker'

export interface DeviceInfo {
  id: string
  label: string
}

export interface DeviceList {
  cameras: DeviceInfo[]
  mics: DeviceInfo[]
  /** 扬声器。Firefox/Safari 不让选输出设备，列表为空 */
  speakers: DeviceInfo[]
}

export type DeviceKind = 'camera' | 'mic' | 'speaker'
const KEYS: Record<DeviceKind, string> = { camera: KEY_CAM, mic: KEY_MIC, speaker: KEY_SPK }

/** 读取已选设备。'' 表示跟随系统默认。 */
export function getPreferred(): { camera: string; mic: string; speaker: string } {
  try {
    return {
      camera: localStorage.getItem(KEY_CAM) ?? '',
      mic: localStorage.getItem(KEY_MIC) ?? '',
      speaker: localStorage.getItem(KEY_SPK) ?? '',
    }
  } catch {
    return { camera: '', mic: '', speaker: '' }
  }
}

/** 保存所选设备。传空串表示恢复成系统默认。 */
export function setPreferred(kind: DeviceKind, id: string): void {
  const key = KEYS[kind]
  try {
    if (id) localStorage.setItem(key, id)
    else localStorage.removeItem(key)
  } catch {
    // 隐私模式下存不住，本次会话内仍然生效。
  }
}

/**
 * 列出可用设备。
 *
 * 没授权过时浏览器只给出空 label（设备条目在、名字是空的），
 * 那样的列表选不了 —— 全是「摄像头 1」「摄像头 2」。所以先取一次权限，
 * 拿到名字后立刻把临时流关掉，不留一个亮着的摄像头。
 */
export async function listDevices(): Promise<DeviceList> {
  if (!navigator.mediaDevices?.enumerateDevices) return { cameras: [], mics: [], speakers: [] }

  let probe: MediaStream | null = null
  try {
    const first = await navigator.mediaDevices.enumerateDevices()
    if (first.some((d) => d.kind !== 'audiooutput' && !d.label)) {
      probe = await navigator.mediaDevices.getUserMedia({ audio: true, video: true })
    }
  } catch {
    // 取不到权限也继续：至少能列出条目，让用户知道设备是有的。
  } finally {
    probe?.getTracks().forEach((t) => t.stop())
  }

  const all = await navigator.mediaDevices.enumerateDevices()
  const pick = (kind: MediaDeviceKind, fallback: string): DeviceInfo[] =>
    all
      .filter((d) => d.kind === kind)
      .map((d, i) => ({ id: d.deviceId, label: d.label || `${fallback} ${i + 1}` }))

  return {
    cameras: pick('videoinput', '摄像头'),
    mics: pick('audioinput', '麦克风'),
    speakers: canPickSpeaker() ? pick('audiooutput', '扬声器') : [],
  }
}

/** 浏览器是否支持选择音频输出设备（setSinkId）。 */
export function canPickSpeaker(): boolean {
  return typeof (HTMLMediaElement.prototype as { setSinkId?: unknown }).setSinkId === 'function'
}

/**
 * 把所选扬声器应用到一个音频元素。
 * 选的设备拔掉了就静默退回默认输出，不能让整堂课没声音。
 */
export async function applySpeaker(el: HTMLMediaElement): Promise<void> {
  const id = getPreferred().speaker
  const sink = el as HTMLMediaElement & { setSinkId?: (id: string) => Promise<void>; sinkId?: string }
  if (!sink.setSinkId || (sink.sinkId ?? '') === id) return
  try { await sink.setSinkId(id) } catch { /* 退回默认 */ }
}

/**
 * 把已选设备并进 getUserMedia 的约束里。
 *
 * 用 ideal 而不是 exact：选中的设备可能已被拔掉，
 * exact 会直接抛错，让用户连默认设备也用不上。
 */
export function applyPreferred(need: MediaStreamConstraints): MediaStreamConstraints {
  const pref = getPreferred()
  if (need.audio && pref.mic) {
    const base = typeof need.audio === 'object' ? need.audio : {}
    need.audio = { ...base, deviceId: { ideal: pref.mic } }
  }
  if (need.video && pref.camera) {
    const base = typeof need.video === 'object' ? need.video : {}
    need.video = { ...base, deviceId: { ideal: pref.camera } }
  }
  return need
}
